/**
 * FairwayBid – Korrekturen an den Rechtstexten aus der Console
 *
 * AGB und Datenschutzerklärung liegen als je ein Markdown-Block in der
 * Console. Zwei Dinge stimmten darin nicht mehr, und beide lassen sich nicht
 * dort pflegen, ohne dass sie beim nächsten Bearbeiten wieder auseinander
 * laufen:
 *
 * 1. Die Betreiberin. Impressum, AGB und Datenschutzerklärung nennen
 *    dieselbe Gesellschaft. Quelle ist deshalb imprint.js – eine Stelle.
 *
 * 2. Die Auftragsverarbeiter. Wir hosten die Portalanwendung selbst; das
 *    Backend mit Konten, Anfragen, Angeboten und Nachrichten liegt weiterhin
 *    bei Sharetribe. Selbst hosten macht die Liste also nicht kürzer,
 *    sondern länger: Der Hoster der Anwendung kommt dazu.
 *
 * Selbstheilend: Jede Regel hat einen Anker im alten Text. Wird der Text in
 * der Console richtiggestellt, greift die Regel nicht mehr und der
 * Console-Text steht unverändert da. Sind alle Anker weg, kann diese Datei
 * weg.
 */
import { BETREIBER, KONTAKT, REGISTER, UST_ID } from './imprint';

/** Die frühere Betreiberin. Beide Apostrophe, gerade und typografisch. */
const ALT_FIRMA = /Vito['‘’]s Sporting Goods GmbH/;
const ALT_FIRMA_ALLE = new RegExp(ALT_FIRMA.source, 'g');
const ALT_MAIL = /t\.stein@vitossportinggoods\.de/g;

/*
  Die zwei Leerzeichen vor dem Zeilenumbruch sind kein Versehen: In Markdown
  wird ein einzelner Umbruch sonst zu einem Leerzeichen, und die Anschrift
  stuende als eine lange Zeile da. Genau so war es vorher auch - das ist der
  Fehler, den wir hier gleich mit erledigen.
*/
const UMBRUCH = '  \n';

/** Anschrift – dieselben Zeilen wie im Impressum. */
const anschriftsAbsatz = () =>
  [BETREIBER.name, BETREIBER.strasse, BETREIBER.plzOrt, BETREIBER.land].join(UMBRUCH);

/**
 * Kontakt und Register.
 *
 * Geschäftsführer und Telefonnummer fallen weg. Handelsregister und
 * USt-IdNr. stehen nur da, wenn sie in imprint.js belegt sind – die alten
 * Werte gehören einer anderen Gesellschaft und dürfen hier nicht stehen.
 */
const kontaktAbsatz = () => {
  const zeilen = [`E-Mail: ${KONTAKT.email}`];
  if (REGISTER.gericht && REGISTER.nummer) {
    zeilen.push(`Handelsregister: ${REGISTER.gericht}, ${REGISTER.nummer}`);
  }
  if (UST_ID) {
    zeilen.push(`Umsatzsteuer-Identifikationsnummer: ${UST_ID}`);
  }
  return zeilen.join(UMBRUCH);
};

/**
 * Ziffer 1 Absatz 2 der AGB – dort steht die Betreiberin im Fließtext und
 * nicht als Anschriftsblock. Der Registerzusatz erscheint erst, wenn die
 * Angaben in imprint.js belegt sind.
 */
const betreiberSatz = () => {
  const registerMaybe =
    REGISTER.gericht && REGISTER.nummer
      ? `, eingetragen im Handelsregister des ${REGISTER.gericht} unter ${REGISTER.nummer}`
      : '';
  return `(2) Betreiberin der Plattform ist die ${BETREIBER.name}, ${BETREIBER.strasse}, ${BETREIBER.plzOrt}${registerMaybe} (nachfolgend „FairwayBid“ oder „Betreiberin“).`;
};

/** Ein Markdown-Text mit aktuellen Betreiberangaben. */
export const mitAktuellerBetreiberin = markdown => {
  if (typeof markdown !== 'string' || !ALT_FIRMA.test(markdown)) {
    return markdown;
  }

  return markdown
    .split('\n\n')
    .map(absatz => {
      // Reihenfolge: der AGB-Satz nennt die Firma ebenfalls und muss deshalb
      // vor der allgemeinen Anschriftsregel geprüft werden.
      if (/^\(2\) Betreiberin der Plattform ist/.test(absatz)) {
        return betreiberSatz();
      }
      if (ALT_FIRMA.test(absatz)) {
        return anschriftsAbsatz();
      }
      if (/^Geschäftsführer:/.test(absatz)) {
        return kontaktAbsatz();
      }
      // Erwähnungen ausserhalb des Betreiberblocks - Name und alte Adresse.
      return absatz.replace(ALT_FIRMA_ALLE, BETREIBER.name).replace(ALT_MAIL, KONTAKT.email);
    })
    .join('\n\n');
};

/**
 * Der Hoster der Portalanwendung.
 *
 * Sharetribe betreibt das Backend – dort liegen Konten, Anfragen, Angebote
 * und Nachrichten. Die Anwendung, über die man das Portal aufruft, hosten
 * wir selbst; dort entstehen die Server-Logfiles. Das ist ein eigener
 * Auftragsverarbeiter und gehört in die Liste.
 *
 * ACHTUNG: Der Satz zur Drittlandübermittlung setzt voraus, dass mit dem
 * Anbieter tatsächlich ein Auftragsverarbeitungsvertrag mit
 * Standardvertragsklauseln geschlossen ist. Der Text kann das nur behaupten –
 * abschließen muss ihn jemand.
 *
 * Wechselt der Hoster, ändert sich hier der Eintrag.
 */
export const FRONTEND_HOSTER = {
  name: 'Render Services, Inc.',
  ort: 'San Francisco, USA',
  zweck:
    'Hosting der Portalanwendung, über die Sie die Plattform aufrufen, einschließlich der dabei anfallenden Server-Logfiles',
  imDrittland: true,
};

const SHARETRIBE_ZEILE = /^- \*\*Sharetribe Oy\*\*/;

/** Genauer als vorher: Sharetribe betreibt nicht "Hosting", sondern das Backend. */
const sharetribeZeile =
  '- **Sharetribe Oy**, Helsinki, Finnland – Betrieb der Marktplatzsoftware; Nutzerkonten, Bedarfsanfragen, Angebote und Nachrichten werden auf den Servern von Sharetribe gespeichert';

const hosterZeile = () =>
  `- **${FRONTEND_HOSTER.name}**, ${FRONTEND_HOSTER.ort} – ${FRONTEND_HOSTER.zweck}`;

const DRITTLAND_ABSATZ = /^Soweit Dienstleister Daten außerhalb der Europäischen Union/;

const drittlandSatz = () =>
  `Das betrifft insbesondere ${FRONTEND_HOSTER.name} (${FRONTEND_HOSTER.ort}). Grundlage der Übermittlung sind die mit dem Anbieter vereinbarten Standardvertragsklauseln.`;

/**
 * Ergänzt die Empfängerliste um den Hoster der Portalanwendung und benennt
 * ihn im Abschnitt zur Drittlandübermittlung.
 *
 * Eigener Anker, unabhängig von der Betreiberkorrektur: Wer den einen Punkt
 * in der Console richtigstellt, soll den anderen nicht mit abschalten.
 */
export const mitHostingHinweisen = markdown => {
  if (typeof markdown !== 'string' || markdown.includes(FRONTEND_HOSTER.name)) {
    return markdown;
  }

  return markdown
    .split('\n\n')
    .map(absatz => {
      if (SHARETRIBE_ZEILE.test(absatz)) {
        // Zeilenweise, damit weitere Einträge in der Liste erhalten bleiben.
        return absatz
          .split('\n')
          .flatMap(zeile =>
            SHARETRIBE_ZEILE.test(zeile) ? [sharetribeZeile, hosterZeile()] : [zeile]
          )
          .join('\n');
      }
      if (FRONTEND_HOSTER.imDrittland && DRITTLAND_ABSATZ.test(absatz)) {
        return `${absatz} ${drittlandSatz()}`;
      }
      return absatz;
    })
    .join('\n\n');
};

/** Beide Korrekturen nacheinander. */
export const korrigiereRechtstext = markdown =>
  mitHostingHinweisen(mitAktuellerBetreiberin(markdown));

/**
 * Läuft durch die Daten einer Console-Seite und wendet die Korrekturen auf
 * jedes Textfeld an. Wir suchen bewusst nach dem Feld "content" statt nach
 * einem festen Pfad: Wie viele Abschnitte und Blöcke die Seite hat, ist eine
 * Entscheidung in der Console und darf sich ändern.
 */
export const mitKorrigiertenRechtstexten = daten => {
  if (Array.isArray(daten)) {
    return daten.map(mitKorrigiertenRechtstexten);
  }
  if (daten && typeof daten === 'object') {
    return Object.fromEntries(
      Object.entries(daten).map(([schluessel, wert]) => [
        schluessel,
        schluessel === 'content' && typeof wert === 'string'
          ? korrigiereRechtstext(wert)
          : mitKorrigiertenRechtstexten(wert),
      ])
    );
  }
  return daten;
};
